import { useEffect, useState } from "react";
import { Cpu, ImageIcon, Upload, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/pages/Dashboard";
import { useAppConfig } from "@/context/ConfigContext";
import { agentImageEdit } from "@/lib/agent";
import { JobProgress } from "@/components/JobProgress";

export default function ImageEdit() {
  const { models } = useAppConfig();
  const editModels = models.filter((m) => (m.capabilities || []).includes("image-edit"));
  const [selectedModel, setSelectedModel] = useState("");
  const [prompt, setPrompt] = useState("");
  const [imageData, setImageData] = useState("");
  const [preview, setPreview] = useState("");
  const [steps, setSteps] = useState(4);
  const [seed, setSeed] = useState(42);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const activeModel = editModels.find((m) => m.id === selectedModel) || editModels[0];

  useEffect(() => {
    if (!loading) return;
    const started = Date.now();
    const timer = setInterval(() => setElapsedSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [loading]);

  const chooseFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setResult({ ok: false, error: "Selecciona una imagen válida." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || "");
      setImageData(value);
      setPreview(value);
      setResult(null);
    };
    reader.readAsDataURL(file);
  };

  const edit = async () => {
    if (!imageData || !prompt.trim() || !editModels.length || loading) return;
    setLoading(true);
    setElapsedSeconds(0);
    setResult(null);
    const data = await agentImageEdit({
      model: selectedModel || editModels[0].id,
      image_data_url: imageData,
      prompt: prompt.trim(),
      steps,
      seed,
    }).catch((e) => ({ ok: false, error: e?.message || "Error de red" }));
    setResult(data);
    setLoading(false);
  };

  useEffect(() => {
    if (editModels.length && !editModels.some((m) => m.id === selectedModel)) setSelectedModel((editModels.find((m) => m.id === "sensenova-u1-5-4bit") || editModels[0]).id);
  }, [editModels, selectedModel]);

  return (
    <div className="krai-enter">
      <PageHeader title="Editar" subtitle={`Edición local · ${activeModel?.name || activeModel?.raw?.name || activeModel?.id || "Selecciona un modelo"}`} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-md border border-[#262626] bg-[#121212] p-4">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">Imagen de entrada</div>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-sm border border-dashed border-[#404040] bg-[#0a0a0a] p-5 text-sm text-[#a3a3a3] hover:text-white">
            <Upload className="h-4 w-4" /> Seleccionar foto
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseFile} className="hidden" />
          </label>
          {preview && <img src={preview} alt="Original" className="max-h-72 w-full rounded-md object-contain bg-black" />}
          <select value={selectedModel || editModels[0]?.id || ""} onChange={(e) => setSelectedModel(e.target.value)} className="h-10 w-full rounded-md border border-[#262626] bg-[#0a0a0a] px-3 text-sm">
            {editModels.map((m) => <option key={m.id} value={m.id}>{m.name || m.raw?.name || m.id}</option>)}
          </select>
          {activeModel?.id === "qwen-image-edit-4bit" && <p className="text-sm text-amber-300">Qwen consume mucha memoria y puede tardar más de 10 minutos en este Mac. SenseNova completó la prueba de edición en unos 76 segundos con el chat pausado.</p>}
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe exactamente qué quieres cambiar…" className="min-h-32 border-[#262626] bg-[#0a0a0a]" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" value={steps} onChange={(e) => setSteps(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" />
            <Input type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" />
          </div>
          <Button onClick={edit} disabled={!imageData || !prompt.trim() || !editModels.length || loading} className="w-full rounded-sm bg-white text-black hover:bg-neutral-200">
            {loading ? <Cpu className="mr-2 h-4 w-4 animate-pulse" /> : <ImageIcon className="mr-2 h-4 w-4" />}
            {loading ? `Editando… ${Math.floor(elapsedSeconds / 60)}:${String(elapsedSeconds % 60).padStart(2, "0")}` : "Editar imagen"}
          </Button>
          <JobProgress watch={loading} />
        </div>
        <div className="min-h-[500px] rounded-md border border-[#262626] bg-[#121212] p-4">
          {!result && <div role="status" className="flex h-full min-h-[460px] items-center justify-center p-6 text-center text-[#737373]">{loading ? "La imagen se está procesando en tu Mac. Puede tardar hasta 40 minutos con Qwen; mantén esta ventana abierta y no inicies otra edición." : "La imagen editada aparecerá aquí."}</div>}
          {result?.ok && <img src={result.image_data_url} alt="Editada localmente" className="w-full rounded-md" />}
          {result && !result.ok && <div className="flex items-start gap-3 rounded-md border border-red-500/40 bg-red-500/10 p-3"><AlertTriangle className="h-4 w-4 text-red-400" /><span className="font-mono text-sm text-red-200">{result.error}</span></div>}
        </div>
      </div>
    </div>
  );
}
